// Public Session Registration Page - Modern Design
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, CheckCircle, AlertCircle, Users } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const currencies = [
  { value: 'EUR', label: 'Euro (€)', symbol: '€' },
  { value: 'USD', label: 'Dollar ($)', symbol: '$' },
  { value: 'TND', label: 'Dinar Tunisien (TND)', symbol: 'TND' }
];

const countries = [
  'France', 'Tunisie', 'Belgique', 'Suisse', 'Canada', 'Maroc', 'Algérie',
  'Sénégal', 'Côte d\'Ivoire', 'Cameroun', 'Luxembourg', 'Allemagne', 'Autre'
];

const sources = [
  'LinkedIn', 'Facebook', 'Instagram', 'Twitter/X', 'Recherche Google',
  'Recommandation d\'un ami', 'Email/Newsletter', 'Site web', 'Autre'
];

// Liste des domaines email valides/connus
const validEmailDomains = [
  // Google
  'gmail.com', 'googlemail.com',
  // Microsoft
  'outlook.com', 'outlook.fr', 'hotmail.com', 'hotmail.fr', 'live.com', 'live.fr', 'msn.com',
  // Yahoo
  'yahoo.com', 'yahoo.fr', 'yahoo.co.uk', 'ymail.com', 'rocketmail.com',
  // Apple
  'icloud.com', 'me.com', 'mac.com',
  // ProtonMail
  'protonmail.com', 'proton.me', 'pm.me',
  // Zoho
  'zoho.com', 'zohomail.com',
  // AOL
  'aol.com', 'aol.fr',
  // French providers
  'orange.fr', 'wanadoo.fr', 'free.fr', 'sfr.fr', 'laposte.net', 'bbox.fr', 'numericable.fr',
  'neuf.fr', 'club-internet.fr', 'alice.fr', 'cegetel.net', 'noos.fr',
  // Belgian providers
  'skynet.be', 'telenet.be', 'proximus.be', 'belgacom.net', 'scarlet.be',
  // Swiss providers
  'bluewin.ch', 'sunrise.ch', 'swisscom.ch', 'gmx.ch',
  // German providers
  'gmx.de', 'gmx.net', 'web.de', 't-online.de', 'freenet.de', 'arcor.de', '1und1.de',
  // Canadian providers
  'rogers.com', 'bell.net', 'telus.net', 'shaw.ca', 'videotron.ca', 'sympatico.ca',
  // North African providers
  'topnet.tn', 'gnet.tn', 'planet.tn', 'hexabyte.tn', 'tunet.tn',
  'menara.ma', 'iam.ma', 'meditel.ma',
  'djezzy.dz', 'mobilis.dz', 'ooredoo.dz',
  // African providers
  'orange.sn', 'orange.ci', 'orange.cm', 'mtn.com',
  // Business/Enterprise
  'company.com', 'entreprise.fr', 'corporate.com',
  // Education
  'edu', 'ac.fr', 'univ-paris.fr', 'ens.fr', 'polytechnique.fr', 'edu.tn',
  // Tech companies
  'amazon.com', 'apple.com', 'microsoft.com', 'google.com', 'facebook.com', 'meta.com',
  // Other popular
  'mail.com', 'email.com', 'inbox.com', 'fastmail.com', 'tutanota.com', 'mailfence.com',
  'yandex.com', 'yandex.ru', 'mail.ru', 'rambler.ru',
  'qq.com', '163.com', '126.com', 'sina.com',
  // Generic TLDs (allow any domain with these)
  'io', 'co', 'tech', 'dev', 'app', 'cloud', 'online', 'digital', 'agency', 'studio',
  'consulting', 'solutions', 'services', 'group', 'team', 'works', 'pro', 'expert',
  'training', 'academy', 'institute', 'school', 'university', 'college',
  'org', 'net', 'info', 'biz'
];

// Fonction pour valider le domaine email
const validateEmailDomain = (email) => {
  if (!email || !email.includes('@')) return { valid: false, domain: null };
  
  const domain = email.split('@')[1]?.toLowerCase();
  if (!domain) return { valid: false, domain: null };
  
  // Vérifier si le domaine exact est dans la liste
  if (validEmailDomains.includes(domain)) {
    return { valid: true, domain };
  }
  
  // Vérifier si c'est un sous-domaine d'un domaine valide
  for (const validDomain of validEmailDomains) {
    if (domain.endsWith('.' + validDomain)) {
      return { valid: true, domain };
    }
  }
  
  // Vérifier l'extension TLD
  const tld = domain.split('.').pop();
  if (validEmailDomains.includes(tld)) {
    return { valid: true, domain };
  }
  
  // Vérifier si c'est un domaine professionnel (contient le nom d'une entreprise)
  // On accepte les domaines avec des extensions courantes
  const commonTlds = ['com', 'fr', 'be', 'ch', 'ca', 'tn', 'ma', 'dz', 'sn', 'ci', 'cm', 'de', 'uk', 'es', 'it', 'nl', 'lu'];
  const domainTld = domain.split('.').pop();
  if (commonTlds.includes(domainTld) && domain.split('.').length >= 2) {
    // C'est probablement un domaine d'entreprise valide
    return { valid: true, domain };
  }
  
  return { valid: false, domain };
};

export default function SessionRegistrationPage() {
  const { slug } = useParams();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const [form, setForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    country: '',
    selected_currency: 'EUR',
    source: '',
    payment_method: 'card'
  });

  const [formErrors, setFormErrors] = useState({});
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [emailDomainWarning, setEmailDomainWarning] = useState(null);

  // Validation en temps réel de l'email
  const handleEmailChange = (e) => {
    const email = e.target.value;
    setForm(prev => ({ ...prev, email }));
    
    // Effacer l'erreur de formulaire si elle existe
    if (formErrors.email) {
      setFormErrors(prev => ({ ...prev, email: null }));
    }
    
    // Vérifier le domaine si l'email contient @
    if (email.includes('@') && email.split('@')[1]?.length > 0) {
      const domainCheck = validateEmailDomain(email);
      if (!domainCheck.valid && domainCheck.domain) {
        setEmailDomainWarning(`Le domaine "${domainCheck.domain}" n'est pas reconnu`);
      } else {
        setEmailDomainWarning(null);
      }
    } else {
      setEmailDomainWarning(null);
    }
  };

  useEffect(() => {
    loadSession();
  }, [slug]);

  const loadSession = async () => {
    try {
      const response = await fetch(`${API_URL}/api/training/public/${slug}`);
      
      if (response.ok) {
        const data = await response.json();
        setSession(data);
        setForm(prev => ({ ...prev, selected_currency: data.primary_currency }));
      } else if (response.status === 404) {
        setError('Session non trouvée');
      } else {
        throw new Error('Load failed');
      }
    } catch (error) {
      console.error('Failed to load session:', error);
      setError('Impossible de charger les informations de la session');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!form.full_name.trim()) errors.full_name = 'Le nom est requis';
    if (!form.email.trim()) {
      errors.email = 'L\'email est requis';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      errors.email = 'Format d\'email invalide';
    } else {
      // Validation du domaine email
      const domainCheck = validateEmailDomain(form.email);
      if (!domainCheck.valid) {
        errors.email = `Le domaine "${domainCheck.domain || 'inconnu'}" n'est pas reconnu. Merci d'utiliser un email valide (ex: gmail.com, outlook.com, etc.)`;
      }
    }
    if (!form.phone.trim()) errors.phone = 'Le téléphone est requis';
    if (!form.country) errors.country = 'Le pays est requis';
    if (!form.source) errors.source = 'Ce champ est requis';
    if (!termsAccepted) errors.terms = 'Vous devez accepter les conditions';
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Veuillez corriger les erreurs');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/training/public/${slug}/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      
      if (response.ok) {
        setSubmitted(true);
        toast.success('Inscription enregistrée !');
      } else {
        const data = await response.json();
        throw new Error(data.detail || 'Inscription échouée');
      }
    } catch (error) {
      console.error('Submit error:', error);
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatShortDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const getSelectedPrice = () => {
    if (!session) return 0;
    return session.prices[form.selected_currency] || 0;
  };

  const getCurrencySymbol = () => {
    return currencies.find(c => c.value === form.selected_currency)?.symbol || '€';
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{background: 'radial-gradient(circle at top left, rgba(37, 99, 235, 0.12), transparent 28%), radial-gradient(circle at bottom right, rgba(59, 130, 246, 0.10), transparent 30%), #f4f7fb'}}>
        <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{background: 'radial-gradient(circle at top left, rgba(37, 99, 235, 0.12), transparent 28%), radial-gradient(circle at bottom right, rgba(59, 130, 246, 0.10), transparent 30%), #f4f7fb'}}>
        <div className="bg-white rounded-3xl shadow-xl p-8 max-w-md text-center">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">Erreur</h2>
          <p className="text-gray-500">{error}</p>
        </div>
      </div>
    );
  }

  // Success state
  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{background: 'radial-gradient(circle at top left, rgba(37, 99, 235, 0.12), transparent 28%), radial-gradient(circle at bottom right, rgba(59, 130, 246, 0.10), transparent 30%), #f4f7fb'}}>
        <div className="bg-white rounded-3xl shadow-xl p-8 max-w-lg text-center">
          <CheckCircle className="h-20 w-20 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">Inscription confirmée !</h2>
          <p className="text-gray-500 mb-4">
            Merci pour votre inscription à <strong>{session.name}</strong>.
          </p>
          <p className="text-sm text-gray-400">
            Vous recevrez un email de confirmation avec les détails de paiement et les prochaines étapes.
          </p>
          <div className="mt-6 p-4 bg-blue-50 rounded-2xl border border-blue-100">
            <p className="text-sm font-medium text-gray-600">Montant à régler:</p>
            <p className="text-3xl font-bold text-blue-600">
              {getSelectedPrice()} {getCurrencySymbol()}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Full state
  if (session.status === 'full') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{background: 'radial-gradient(circle at top left, rgba(37, 99, 235, 0.12), transparent 28%), radial-gradient(circle at bottom right, rgba(59, 130, 246, 0.10), transparent 30%), #f4f7fb'}}>
        <div className="bg-white rounded-3xl shadow-xl p-8 max-w-lg text-center">
          <Users className="h-20 w-20 text-orange-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">Session complète</h2>
          <p className="text-gray-500 mb-4">
            Toutes les places pour <strong>{session.name}</strong> ont été prises.
          </p>
          <p className="text-sm text-gray-400">
            Contactez-nous pour être informé des prochaines sessions.
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        :root {
          --bg: #f4f7fb;
          --surface: #ffffff;
          --surface-soft: #f8fafc;
          --text: #0f172a;
          --muted: #64748b;
          --border: #e2e8f0;
          --primary: #2563eb;
          --primary-dark: #1d4ed8;
          --primary-soft: #dbeafe;
          --success: #16a34a;
          --success-soft: #dcfce7;
          --warning: #f59e0b;
          --warning-soft: #fef3c7;
          --error: #dc2626;
          --shadow-lg: 0 20px 50px rgba(15, 23, 42, 0.10);
          --shadow-md: 0 10px 30px rgba(15, 23, 42, 0.08);
          --radius-xl: 24px;
          --radius-lg: 18px;
          --radius-md: 14px;
          --radius-sm: 10px;
        }

        .reg-page {
          min-height: 100vh;
          padding: 48px 20px;
          font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
          color: var(--text);
          background:
            radial-gradient(circle at top left, rgba(37, 99, 235, 0.12), transparent 28%),
            radial-gradient(circle at bottom right, rgba(59, 130, 246, 0.10), transparent 30%),
            var(--bg);
          line-height: 1.5;
        }

        .reg-wrapper {
          max-width: 1280px;
          margin: 0 auto;
        }

        .reg-hero {
          text-align: center;
          margin-bottom: 32px;
        }

        .reg-hero-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 8px 14px;
          margin-bottom: 14px;
          border-radius: 999px;
          background: rgba(255, 255, 255, 0.8);
          border: 1px solid rgba(226, 232, 240, 0.9);
          color: var(--primary-dark);
          font-size: 13px;
          font-weight: 700;
          letter-spacing: 0.02em;
          box-shadow: var(--shadow-md);
          backdrop-filter: blur(10px);
        }

        .reg-hero h1 {
          margin: 0;
          font-size: clamp(32px, 5vw, 48px);
          line-height: 1.1;
          letter-spacing: -0.03em;
        }

        .reg-hero p {
          max-width: 760px;
          margin: 14px auto 0;
          color: var(--muted);
          font-size: 17px;
        }

        .reg-layout {
          display: grid;
          grid-template-columns: 1.2fr 0.9fr;
          gap: 26px;
          align-items: start;
        }

        .reg-panel {
          background: rgba(255, 255, 255, 0.88);
          border: 1px solid rgba(226, 232, 240, 0.9);
          border-radius: var(--radius-xl);
          box-shadow: var(--shadow-lg);
          backdrop-filter: blur(14px);
          overflow: hidden;
        }

        .reg-panel-inner {
          padding: 30px;
        }

        .reg-session-head {
          display: flex;
          flex-wrap: wrap;
          justify-content: space-between;
          gap: 16px;
          margin-bottom: 26px;
        }

        .reg-session-head h2 {
          margin: 0 0 10px;
          font-size: clamp(24px, 4vw, 34px);
          line-height: 1.15;
          letter-spacing: -0.02em;
        }

        .reg-session-desc {
          margin: 0;
          color: var(--muted);
          max-width: 720px;
          font-size: 15px;
        }

        .reg-badges {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          align-items: flex-start;
        }

        .reg-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 10px 14px;
          border-radius: 999px;
          font-size: 13px;
          font-weight: 700;
          line-height: 1;
          white-space: nowrap;
          border: 1px solid transparent;
        }

        .reg-badge-primary {
          background: var(--primary-soft);
          color: var(--primary-dark);
          border-color: #bfdbfe;
        }

        .reg-badge-success {
          background: var(--success-soft);
          color: #166534;
          border-color: #bbf7d0;
        }

        .reg-badge-warning {
          background: var(--warning-soft);
          color: #92400e;
          border-color: #fde68a;
        }

        .reg-mini-stats {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 12px;
          margin-bottom: 24px;
        }

        .reg-mini-stat {
          background: linear-gradient(180deg, #ffffff, #f8fbff);
          border: 1px solid var(--border);
          border-radius: 16px;
          padding: 16px 14px;
          text-align: center;
        }

        .reg-mini-stat strong {
          display: block;
          font-size: 18px;
          margin-bottom: 4px;
          letter-spacing: -0.02em;
        }

        .reg-mini-stat span {
          color: var(--muted);
          font-size: 12px;
        }

        .reg-summary-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 14px;
          margin-bottom: 26px;
        }

        .reg-summary-card {
          background: var(--surface-soft);
          border: 1px solid var(--border);
          border-radius: var(--radius-lg);
          padding: 18px;
          box-shadow: 0 6px 18px rgba(15, 23, 42, 0.04);
        }

        .reg-summary-label {
          color: var(--muted);
          font-size: 13px;
          margin-bottom: 8px;
        }

        .reg-summary-value {
          font-size: 16px;
          font-weight: 700;
          color: var(--text);
        }

        .reg-section {
          margin-top: 22px;
          padding-top: 22px;
          border-top: 1px solid var(--border);
        }

        .reg-section:first-of-type {
          margin-top: 0;
          padding-top: 0;
          border-top: 0;
        }

        .reg-section-title {
          display: flex;
          align-items: center;
          gap: 10px;
          margin: 0 0 14px;
          font-size: 18px;
          letter-spacing: -0.01em;
        }

        .reg-section-icon {
          width: 34px;
          height: 34px;
          border-radius: 10px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: var(--primary-soft);
          color: var(--primary-dark);
          font-size: 15px;
          font-weight: 800;
          flex-shrink: 0;
        }

        .reg-list {
          list-style: none;
          margin: 0;
          padding: 0;
          display: grid;
          gap: 10px;
        }

        .reg-list li {
          position: relative;
          padding-left: 18px;
          color: #334155;
        }

        .reg-list li::before {
          content: "";
          position: absolute;
          left: 0;
          top: 10px;
          width: 7px;
          height: 7px;
          border-radius: 999px;
          background: var(--primary);
          box-shadow: 0 0 0 4px rgba(37, 99, 235, 0.10);
        }

        .reg-form-panel {
          position: sticky;
          top: 24px;
        }

        .reg-form-header {
          padding: 30px 30px 0;
        }

        .reg-form-header h3 {
          margin: 0 0 8px;
          font-size: 28px;
          letter-spacing: -0.02em;
        }

        .reg-form-header p {
          margin: 0;
          color: var(--muted);
          font-size: 15px;
        }

        .reg-form-body {
          padding: 24px 30px 30px;
        }

        .reg-notice {
          display: flex;
          gap: 12px;
          align-items: flex-start;
          margin-bottom: 22px;
          padding: 14px 16px;
          background: #f8fafc;
          border: 1px solid var(--border);
          border-radius: var(--radius-md);
          color: #334155;
          font-size: 14px;
        }

        .reg-notice-dot {
          width: 10px;
          height: 10px;
          border-radius: 999px;
          background: var(--success);
          margin-top: 5px;
          flex-shrink: 0;
          box-shadow: 0 0 0 6px rgba(22, 163, 74, 0.10);
        }

        .reg-form-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }

        .reg-field {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .reg-field.full {
          grid-column: 1 / -1;
        }

        .reg-field label {
          font-size: 14px;
          font-weight: 700;
          color: #1e293b;
        }

        .reg-input,
        .reg-select {
          width: 100%;
          border: 1px solid var(--border);
          background: #fff;
          border-radius: 14px;
          padding: 14px 15px;
          font-size: 15px;
          color: var(--text);
          outline: none;
          transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
        }

        .reg-input::placeholder {
          color: #94a3b8;
        }

        .reg-input:focus,
        .reg-select:focus {
          border-color: #93c5fd;
          box-shadow: 0 0 0 4px rgba(37, 99, 235, 0.12);
          transform: translateY(-1px);
        }

        .reg-input.error,
        .reg-select.error {
          border-color: var(--error);
        }

        .reg-field-error {
          font-size: 12px;
          color: var(--error);
        }

        .reg-field-warning {
          font-size: 12px;
          color: #d97706;
          background-color: #fef3c7;
          padding: 8px 12px;
          border-radius: 8px;
          border-left: 3px solid #f59e0b;
          margin-top: 4px;
          display: block;
        }

        .reg-checkbox-wrap {
          display: flex;
          gap: 12px;
          align-items: flex-start;
          padding: 14px 16px;
          margin-top: 4px;
          border: 1px solid var(--border);
          border-radius: var(--radius-md);
          background: #fbfdff;
        }

        .reg-checkbox-wrap input {
          margin-top: 3px;
          width: 16px;
          height: 16px;
          accent-color: var(--primary);
          flex-shrink: 0;
        }

        .reg-checkbox-wrap label {
          margin: 0;
          font-size: 14px;
          font-weight: 500;
          color: #334155;
        }

        .reg-payment-methods {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
        }

        .reg-payment-option {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 16px;
          border: 2px solid var(--border);
          border-radius: var(--radius-md);
          cursor: pointer;
          transition: all 0.2s ease;
          background: #fff;
        }

        .reg-payment-option:hover {
          border-color: #93c5fd;
          background: #f8fafc;
        }

        .reg-payment-option.selected {
          border-color: var(--primary);
          background: var(--primary-soft);
        }

        .reg-payment-option input {
          display: none;
        }

        .reg-payment-icon {
          font-size: 24px;
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #f1f5f9;
          border-radius: 10px;
        }

        .reg-payment-option.selected .reg-payment-icon {
          background: #fff;
        }

        .reg-payment-info {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .reg-payment-info strong {
          font-size: 14px;
          font-weight: 700;
          color: var(--text);
        }

        .reg-payment-info span {
          font-size: 12px;
          color: var(--muted);
        }

        .reg-actions {
          display: flex;
          flex-direction: column;
          gap: 12px;
          margin-top: 22px;
        }

        .reg-button {
          display: inline-flex;
          justify-content: center;
          align-items: center;
          gap: 10px;
          width: 100%;
          border: 0;
          border-radius: 16px;
          padding: 16px 20px;
          cursor: pointer;
          font-size: 15px;
          font-weight: 800;
          text-decoration: none;
          transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
        }

        .reg-button-primary {
          color: #fff;
          background: linear-gradient(135deg, var(--primary), var(--primary-dark));
          box-shadow: 0 14px 24px rgba(37, 99, 235, 0.22);
        }

        .reg-button-primary:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 18px 28px rgba(37, 99, 235, 0.28);
        }

        .reg-button-primary:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .reg-footnote {
          margin: 4px 0 0;
          color: var(--muted);
          font-size: 12px;
          text-align: center;
        }

        .reg-price-display {
          background: linear-gradient(135deg, #eff6ff, #ffffff);
          border: 1px solid #dbeafe;
          border-radius: var(--radius-lg);
          padding: 18px;
          text-align: center;
          margin-bottom: 16px;
        }

        .reg-price-display .label {
          font-size: 14px;
          color: var(--muted);
          margin-bottom: 4px;
        }

        .reg-price-display .amount {
          font-size: 28px;
          font-weight: 800;
          color: var(--primary-dark);
        }

        @media (max-width: 1100px) {
          .reg-layout {
            grid-template-columns: 1fr;
          }

          .reg-form-panel {
            position: static;
          }
        }

        @media (max-width: 720px) {
          .reg-page {
            padding: 28px 14px;
          }

          .reg-panel-inner,
          .reg-form-header,
          .reg-form-body {
            padding-left: 20px;
            padding-right: 20px;
          }

          .reg-summary-grid,
          .reg-form-grid,
          .reg-mini-stats {
            grid-template-columns: 1fr;
          }

          .reg-session-head {
            flex-direction: column;
          }

          .reg-hero p {
            font-size: 15px;
          }
        }
      `}</style>

      <main className="reg-page">
        <div className="reg-wrapper">
          <header className="reg-hero">
            <div className="reg-hero-badge">Session de formation ouverte aux inscriptions</div>
            <h1>Réservez votre place en quelques minutes</h1>
            <p>
              Découvrez tous les détails de la session ci-dessous et complétez votre inscription
              pour rejoindre une formation structurée, pratique et orientée résultats.
            </p>
          </header>

          <section className="reg-layout">
            {/* Session Info Panel */}
            <article className="reg-panel">
              <div className="reg-panel-inner">
                <div className="reg-session-head">
                  <div>
                    <h2>{session.name}</h2>
                    <p className="reg-session-desc">
                      {session.description || `Formation ${session.course_type_name} - Une session conçue pour vous permettre d'acquérir des compétences pratiques et directement applicables.`}
                    </p>
                  </div>

                  <div className="reg-badges">
                    <span className="reg-badge reg-badge-success">Session ouverte</span>
                    <span className="reg-badge reg-badge-primary">{session.course_type_name}</span>
                    {session.spots_remaining <= 5 && (
                      <span className="reg-badge reg-badge-warning">Places limitées</span>
                    )}
                  </div>
                </div>

                <div className="reg-mini-stats">
                  <div className="reg-mini-stat">
                    <strong>{session.spots_remaining}</strong>
                    <span>Places restantes</span>
                  </div>
                  <div className="reg-mini-stat">
                    <strong>{session.max_candidates}</strong>
                    <span>Capacité totale</span>
                  </div>
                  <div className="reg-mini-stat">
                    <strong>{session.prices?.EUR || 0} €</strong>
                    <span>Tarif par participant</span>
                  </div>
                </div>

                <div className="reg-summary-grid">
                  <div className="reg-summary-card">
                    <div className="reg-summary-label">Date de début</div>
                    <div className="reg-summary-value">{formatShortDate(session.start_date)}</div>
                  </div>

                  <div className="reg-summary-card">
                    <div className="reg-summary-label">Date de fin</div>
                    <div className="reg-summary-value">{formatShortDate(session.end_date)}</div>
                  </div>

                  <div className="reg-summary-card">
                    <div className="reg-summary-label">Format</div>
                    <div className="reg-summary-value">{session.course_type_name}</div>
                  </div>

                  <div className="reg-summary-card">
                    <div className="reg-summary-label">Statut</div>
                    <div className="reg-summary-value">Inscriptions ouvertes</div>
                  </div>
                </div>

                {session.module_names && session.module_names.length > 0 && (
                  <section className="reg-section">
                    <h3 className="reg-section-title">
                      <span className="reg-section-icon">01</span>
                      Modules inclus
                    </h3>
                    <ul className="reg-list">
                      {session.module_names.map((module, idx) => (
                        <li key={idx}>{module}</li>
                      ))}
                    </ul>
                  </section>
                )}

                <section className="reg-section">
                  <h3 className="reg-section-title">
                    <span className="reg-section-icon">02</span>
                    Tarifs disponibles
                  </h3>
                  <div className="reg-summary-grid">
                    {currencies.map(c => (
                      <div key={c.value} className="reg-summary-card">
                        <div className="reg-summary-label">{c.label}</div>
                        <div className="reg-summary-value">{session.prices?.[c.value] || 0} {c.symbol}</div>
                      </div>
                    ))}
                  </div>
                </section>
              </div>
            </article>

            {/* Registration Form Panel */}
            <aside className="reg-panel reg-form-panel">
              <div className="reg-form-header">
                <h3>Formulaire d'inscription</h3>
                <p>
                  Remplissez vos informations pour réserver votre place à cette session de formation.
                </p>
              </div>

              <div className="reg-form-body">
                <div className="reg-notice">
                  <span className="reg-notice-dot"></span>
                  <div>
                    Votre demande sera examinée par notre équipe. Vous recevrez un email de confirmation
                    avec les prochaines étapes et les modalités de paiement.
                  </div>
                </div>

                <div className="reg-price-display">
                  <div className="label">Montant à régler</div>
                  <div className="amount">{getSelectedPrice()} {getCurrencySymbol()}</div>
                </div>

                <form onSubmit={handleSubmit}>
                  <div className="reg-form-grid">
                    <div className="reg-field full">
                      <label htmlFor="fullname">Nom complet *</label>
                      <input
                        className={`reg-input ${formErrors.full_name ? 'error' : ''}`}
                        type="text"
                        id="fullname"
                        value={form.full_name}
                        onChange={(e) => setForm(prev => ({ ...prev, full_name: e.target.value }))}
                        placeholder="Ex. Sarah El Mansouri"
                      />
                      {formErrors.full_name && <span className="reg-field-error">{formErrors.full_name}</span>}
                    </div>

                    <div className="reg-field">
                      <label htmlFor="email">Email *</label>
                      <input
                        className={`reg-input ${formErrors.email || emailDomainWarning ? 'error' : ''}`}
                        type="email"
                        id="email"
                        value={form.email}
                        onChange={handleEmailChange}
                        placeholder="votre@email.com"
                      />
                      {formErrors.email && <span className="reg-field-error">{formErrors.email}</span>}
                      {!formErrors.email && emailDomainWarning && (
                        <span className="reg-field-warning">{emailDomainWarning}. Utilisez un email reconnu (gmail.com, outlook.com, etc.)</span>
                      )}
                    </div>

                    <div className="reg-field">
                      <label htmlFor="phone">Téléphone *</label>
                      <input
                        className={`reg-input ${formErrors.phone ? 'error' : ''}`}
                        type="tel"
                        id="phone"
                        value={form.phone}
                        onChange={(e) => setForm(prev => ({ ...prev, phone: e.target.value }))}
                        placeholder="+33 6 12 34 56 78"
                      />
                      {formErrors.phone && <span className="reg-field-error">{formErrors.phone}</span>}
                    </div>

                    <div className="reg-field">
                      <label htmlFor="country">Pays *</label>
                      <select
                        className={`reg-select ${formErrors.country ? 'error' : ''}`}
                        id="country"
                        value={form.country}
                        onChange={(e) => setForm(prev => ({ ...prev, country: e.target.value }))}
                      >
                        <option value="">Sélectionner...</option>
                        {countries.map(country => (
                          <option key={country} value={country}>{country}</option>
                        ))}
                      </select>
                      {formErrors.country && <span className="reg-field-error">{formErrors.country}</span>}
                    </div>

                    <div className="reg-field">
                      <label htmlFor="currency">Devise de paiement</label>
                      <select
                        className="reg-select"
                        id="currency"
                        value={form.selected_currency}
                        onChange={(e) => setForm(prev => ({ ...prev, selected_currency: e.target.value }))}
                      >
                        {currencies.map(c => (
                          <option key={c.value} value={c.value}>{c.label}</option>
                        ))}
                      </select>
                    </div>

                    <div className="reg-field full">
                      <label htmlFor="source">Comment nous avez-vous connu ? *</label>
                      <select
                        className={`reg-select ${formErrors.source ? 'error' : ''}`}
                        id="source"
                        value={form.source}
                        onChange={(e) => setForm(prev => ({ ...prev, source: e.target.value }))}
                      >
                        <option value="">Sélectionner...</option>
                        {sources.map(source => (
                          <option key={source} value={source}>{source}</option>
                        ))}
                      </select>
                      {formErrors.source && <span className="reg-field-error">{formErrors.source}</span>}
                    </div>

                    <div className="reg-field full">
                      <label htmlFor="payment_method">Mode de paiement préféré *</label>
                      <div className="reg-payment-methods">
                        <label className={`reg-payment-option ${form.payment_method === 'card' ? 'selected' : ''}`}>
                          <input
                            type="radio"
                            name="payment_method"
                            value="card"
                            checked={form.payment_method === 'card'}
                            onChange={(e) => setForm(prev => ({ ...prev, payment_method: e.target.value }))}
                          />
                          <div className="reg-payment-icon">💳</div>
                          <div className="reg-payment-info">
                            <strong>Paiement par carte</strong>
                            <span>Visa, Mastercard, etc.</span>
                          </div>
                        </label>
                        <label className={`reg-payment-option ${form.payment_method === 'bank_transfer' ? 'selected' : ''}`}>
                          <input
                            type="radio"
                            name="payment_method"
                            value="bank_transfer"
                            checked={form.payment_method === 'bank_transfer'}
                            onChange={(e) => setForm(prev => ({ ...prev, payment_method: e.target.value }))}
                          />
                          <div className="reg-payment-icon">🏦</div>
                          <div className="reg-payment-info">
                            <strong>Virement bancaire</strong>
                            <span>IBAN envoyé par email</span>
                          </div>
                        </label>
                      </div>
                    </div>

                    <div className="reg-field full">
                      <div className="reg-checkbox-wrap">
                        <input 
                          type="checkbox" 
                          id="terms" 
                          checked={termsAccepted}
                          onChange={(e) => setTermsAccepted(e.target.checked)}
                        />
                        <label htmlFor="terms">
                          J'accepte les conditions d'inscription et j'autorise le traitement
                          de mes données dans le cadre de cette demande.
                        </label>
                      </div>
                      {formErrors.terms && <span className="reg-field-error">{formErrors.terms}</span>}
                    </div>
                  </div>

                  <div className="reg-actions">
                    <button 
                      className="reg-button reg-button-primary" 
                      type="submit"
                      disabled={submitting}
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="h-5 w-5 animate-spin" />
                          Inscription en cours...
                        </>
                      ) : (
                        <>
                          <CheckCircle className="h-5 w-5" />
                          S'inscrire à cette session
                        </>
                      )}
                    </button>
                    <p className="reg-footnote">
                      Confirmation sous 24 à 48 heures ouvrées selon les disponibilités.
                    </p>
                  </div>
                </form>
              </div>
            </aside>
          </section>
        </div>
      </main>
    </>
  );
}
